import React, { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import * as firestore from 'firebase/firestore';
import AuthLayout from '../../components/auth/AuthLayout';
import { auth, db } from '../../../firebaseConfig.js';
import { normalizeEmail, validateEmail } from '../../lib/validation/email';
import { normalizeUsername, validateUsername } from '../../lib/validation/username';
import { checkUsernameAvailability } from '../../lib/usernameAvailability';

const Step2 = ({ onNavigate }) => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [prefillLoaded, setPrefillLoaded] = useState(false);

  const [username, setUsername] = useState('');
  const [usernameLocked, setUsernameLocked] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState('idle');
  const [usernameStatusMessage, setUsernameStatusMessage] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);

  const [contactEmail, setContactEmail] = useState('');

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    if (!user) {
      onNavigate('login', null, { replace: true });
    }
  }, [authChecked, user, onNavigate]);

  useEffect(() => {
    if (!user || prefillLoaded) return;

    let isMounted = true;
    const loadProfile = async () => {
      try {
        const snap = await firestore.getDoc(firestore.doc(db, 'users', user.uid));
        if (!isMounted) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          const existingEmail = data.contactEmail || user.email || '';
          setContactEmail((prev) => prev || existingEmail);

          if (data.username) {
            setUsername(data.username);
            setUsernameLocked(true);
            setUsernameStatus('locked');
            setUsernameStatusMessage('Reserved for you.');
          } else if (data.usernameLower) {
            setUsername(data.usernameLower);
            setUsernameLocked(true);
            setUsernameStatus('locked');
            setUsernameStatusMessage('Reserved for you.');
          }
        } else {
          setContactEmail((prev) => prev || user.email || '');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[onboarding] Failed to load contact data', error);
        }
      } finally {
        if (isMounted) {
          setPrefillLoaded(true);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [user, prefillLoaded]);

  const getFieldErrors = () => {
    const errors = {};
    if (!usernameLocked) {
      const usernameValidation = validateUsername(username);
      if (!usernameValidation.ok) {
        errors.username = usernameValidation.reason || 'Enter a valid username.';
      }
    } else if (!username) {
      errors.username = 'Username is required.';
    }

    const emailValidation = validateEmail(contactEmail);
    if (!emailValidation.ok) {
      errors.contactEmail = emailValidation.reason || 'Enter a valid email address.';
    }
    return errors;
  };

  const isUsernameAvailable = useMemo(() => {
    return usernameLocked || usernameStatus === 'available' || usernameStatus === 'reservedByYou';
  }, [usernameLocked, usernameStatus]);

  const isUsernameTaken = usernameStatus === 'taken';
  const isUsernameInvalid = usernameStatus === 'invalid';

  const checkAvailability = async () => {
    if (!user || usernameLocked) return;
    const validation = validateUsername(username);
    if (!validation.ok) {
      setUsernameStatus('invalid');
      setUsernameStatusMessage(validation.reason || 'Enter a valid username.');
      return;
    }

    const usernameLower = normalizeUsername(username);
    setCheckingUsername(true);
    setUsernameStatus('checking');
    setUsernameStatusMessage('');

    try {
      const result = await checkUsernameAvailability(db, usernameLower, user.uid);
      setUsernameStatus(result.status);
      if (result.status === 'taken') {
        setUsernameStatusMessage('Username taken.');
      } else if (result.status === 'reservedByYou') {
        setUsernameStatusMessage('This is your username.');
      } else if (result.status === 'available') {
        setUsernameStatusMessage('Available.');
      } else {
        setUsernameStatusMessage('Enter a valid username.');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[onboarding] Failed to check username availability', error);
      }
      setUsernameStatus('invalid');
      setUsernameStatusMessage('Could not check availability. Try again.');
    } finally {
      setCheckingUsername(false);
    }
  };

  useEffect(() => {
    if (!user || usernameLocked) return;

    if (!username.trim()) {
      setUsernameStatus('idle');
      setUsernameStatusMessage('');
      return;
    }

    const validation = validateUsername(username);
    if (!validation.ok) {
      setUsernameStatus('invalid');
      setUsernameStatusMessage(validation.reason || 'Enter a valid username.');
      return;
    }

    const debounce = setTimeout(() => {
      checkAvailability();
    }, 500);

    return () => clearTimeout(debounce);
  }, [username, user, usernameLocked]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitAttempted(true);
    setSubmitError('');

    const errors = getFieldErrors();
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!user) {
      onNavigate('login');
      return;
    }

    setLoading(true);

    try {
      const usernameLower = normalizeUsername(username);
      const normalizedEmail = normalizeEmail(contactEmail);

      await firestore.runTransaction(db, async (transaction) => {
        const usernameRef = firestore.doc(db, 'usernames', usernameLower);
        const profileRef = firestore.doc(db, 'users', user.uid);
        const usernameSnap = await transaction.get(usernameRef);

        if (usernameSnap.exists()) {
          const data = usernameSnap.data() || {};
          if (data.uid && data.uid !== user.uid) {
            throw new Error('USERNAME_TAKEN');
          }
        } else {
          const usersRef = firestore.collection(db, 'users');
          const byLower = await firestore.getDocs(firestore.query(usersRef, firestore.where('usernameLower', '==', usernameLower), firestore.limit(1)));
          if (!byLower.empty && byLower.docs[0].id !== user.uid) {
            throw new Error('USERNAME_TAKEN');
          }
          const byUsername = await firestore.getDocs(firestore.query(usersRef, firestore.where('username', '==', usernameLower), firestore.limit(1)));
          if (!byUsername.empty && byUsername.docs[0].id !== user.uid) {
            throw new Error('USERNAME_TAKEN');
          }

          transaction.set(usernameRef, {
            uid: user.uid,
            createdAt: firestore.serverTimestamp(),
          });
        }

        transaction.set(
          profileRef,
          {
            username: usernameLower,
            usernameLower,
            contactEmail: normalizedEmail,
            onboarding: {
              step: 2,
              status: 'in_progress',
              updatedAt: firestore.serverTimestamp(),
            },
          },
          { merge: true }
        );
      });

      // Also sync username and contactEmail to influencers collection
      try {
        const influencerRef = firestore.doc(db, 'influencers', user.uid);
        const influencerExisting = await firestore.getDoc(influencerRef);
        const existingInfluencer = influencerExisting.exists() ? influencerExisting.data() : {};
        const existingIdentity = existingInfluencer?.identity || {};

        const influencerData = {
          userId: user.uid,
          username: usernameLower,
          contactEmail: normalizedEmail,
          updatedAt: firestore.serverTimestamp(),
          identity: {
            ...existingIdentity,
            handle: usernameLower,
          },
        };

        // Preserve existing fields
        if (existingInfluencer?.displayName) {
          influencerData.displayName = existingInfluencer.displayName;
          influencerData.identity.displayName = existingInfluencer.displayName;
        }
        if (existingInfluencer?.photoURL) {
          influencerData.photoURL = existingInfluencer.photoURL;
          influencerData.profileImageUrl = existingInfluencer.profileImageUrl || existingInfluencer.photoURL;
          influencerData.avatarUrl = existingInfluencer.avatarUrl || existingInfluencer.photoURL;
          influencerData.identity.photoURL = existingInfluencer.photoURL;
        }

        // Preserve createdAt if exists
        if (existingInfluencer?.createdAt) {
          influencerData.createdAt = existingInfluencer.createdAt;
        } else {
          influencerData.createdAt = firestore.serverTimestamp();
        }

        await firestore.setDoc(influencerRef, influencerData, { merge: true });
        console.log('[onboarding] Step2 saved influencer doc uid=' + user.uid);
      } catch (influencerError) {
        console.error('[onboarding] Failed to save to influencers collection:', influencerError?.message ?? influencerError);
        setSubmitError('We could not save your profile. Please try again.');
        setLoading(false);
        return;
      }

      onNavigate('onboarding/step-3');
    } catch (error) {
      if (error?.message === 'USERNAME_TAKEN') {
        setSubmitError('That username is already taken. Please choose another.');
        setUsernameStatus('taken');
      } else {
        console.error('[onboarding] Failed to save step 2', error);
        setSubmitError('We could not save your account details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fieldErrors = getFieldErrors();
  const isEmailValid = validateEmail(contactEmail).ok;
  const isUsernameValid = validateUsername(username).ok || usernameLocked;
  const isSubmitDisabled = loading || !isEmailValid || !isUsernameValid || isUsernameTaken || isUsernameInvalid;

  if (!authChecked) {
    return (
      <AuthLayout
        title="Choose your InfluenceHub username"
        subtitle="Step 2 of 3 — Account"
        cardClassName="px-8 py-6"
      >
        <div className="text-center text-sm text-slate-500">Loading...</div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose your InfluenceHub username"
      subtitle="Step 2 of 3 — Account"
      cardClassName="px-8 py-6"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="username" className="text-sm font-medium text-slate-700">
            Username
          </label>
          <div className="mt-1 flex items-center rounded-md border border-slate-300 focus-within:border-[#1B9DA2] focus-within:ring-1 focus-within:ring-[#1B9DA2]">
            <span className="pl-3 pr-0 text-sm text-slate-400">influencehub.net/u/</span>
            <div className="relative flex-1">
              <input
                id="username"
                name="username"
                type="text"
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  setUsernameStatus('idle');
                  setUsernameStatusMessage('');
                  if (submitError) setSubmitError('');
                }}
                disabled={usernameLocked}
                className="h-11 w-full rounded-md border-0 px-3 pl-0 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                placeholder="username"
                autoComplete="off"
              />
              {isUsernameAvailable && (
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600">
                  ✓
                </span>
              )}
              {isUsernameTaken || isUsernameInvalid ? (
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                  ✕
                </span>
              ) : null}
            </div>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            This will be your InfluenceHub profile URL. Users will be able to find you by this username.
          </p>
          <div className="mt-1 min-h-[18px] text-sm text-red-500">
            {submitAttempted && fieldErrors.username ? fieldErrors.username : ''}
          </div>
          {isUsernameTaken ? (
            <div className="mt-1 text-sm text-red-500">Username taken.</div>
          ) : null}
          {usernameStatus === 'reservedByYou' ? (
            <div className="mt-1 text-sm text-emerald-600">This is your username.</div>
          ) : null}
        </div>

        <div>
          <label htmlFor="contactEmail" className="text-sm font-medium text-slate-700">
            Brand Contact Email
          </label>
          <input
            id="contactEmail"
            name="contactEmail"
            type="email"
            value={contactEmail}
            onChange={(event) => setContactEmail(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="you@domain.com"
            autoComplete="email"
          />
          <p className="mt-2 text-sm text-slate-500">
            This is what brands will see on your profile.
          </p>
          <div className="mt-1 min-h-[18px] text-sm text-red-500">
            {submitAttempted && fieldErrors.contactEmail ? fieldErrors.contactEmail : ''}
          </div>
        </div>

        {submitError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {submitError}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={isSubmitDisabled}
          className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Continue'}
        </button>

        <button
          type="button"
          onClick={() => onNavigate('onboarding/step-1')}
          className="w-full text-sm font-medium text-slate-500 transition hover:text-[#7842D6]"
        >
          Back
        </button>
      </form>
    </AuthLayout>
  );
};

export default Step2;
